import React, { Fragment } from 'react';
import styled from 'styled-components';
import {
  ICohortFieldsFragment,
  Maybe,
  IFilterFieldsFragment,
  ICohortSampleOperation,
} from '../../../../utils/apollo/graphqlSchema';
import * as BuilderInfoUI from '../../BuilderInfoUI';
import InfoPopupHeaderSetting from '../InfoPopupHeaderSetting';
import PerformedCohortDetails from './PerformedCohortDetails';
import MeasureCohortDetails from './MeasureCohortDetails';
import ExpressionCohortDetails from './ExpressionCohortDetails';
import FunnelCohortDetails from './FunnelCohortDetails';
import RetentionCohortDetails from './RetentionCohortDetails';
import FilterInfo from '../FilterInfo';
import { assertNotNil } from '../../../../utils/types';
import {
  ConnectorContainer,
  ConnectorWrapper,
} from '../../../Builder/BuilderUI';

interface IProps {
  cohort: ICohortFieldsFragment;
  sampleOperation: ICohortSampleOperation | null;
  samplePercentage: number | null;
  sampleAbsolute: number | null;
}

const Container = styled(BuilderInfoUI.Content)`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;
const StyledOperation = styled(ConnectorWrapper)`
  padding: 3px 4px;
`;

export default ({
  cohort,
  sampleOperation,
  samplePercentage,
  sampleAbsolute,
}: IProps) => {
  const { schema } = cohort;

  const renderFilterDetails = (filter: Maybe<IFilterFieldsFragment>) => {
    if (!filter) {
      return null;
    }

    return <FilterInfo filter={filter} />;
  };

  const renderCohortDetailsByType = (
    cohortArg: ICohortFieldsFragment,
    cohortIndex: number,
  ) => {
    if (cohortArg.__typename === 'ExpressionCohort') {
      return (
        <>
          <ExpressionCohortDetails
            cohort={cohortArg}
            cohortIndex={cohortIndex}
          />
          {renderFilterDetails(cohortArg.filter)}
        </>
      );
    }

    if (cohortArg.__typename === 'FunnelCohort') {
      return (
        <>
          <FunnelCohortDetails cohort={cohortArg} cohortIndex={cohortIndex} />
          {renderFilterDetails(cohortArg.filter)}
        </>
      );
    }

    if (cohortArg.__typename === 'MeasureCohort') {
      return (
        <>
          <MeasureCohortDetails cohort={cohortArg} cohortIndex={cohortIndex} />
          {renderFilterDetails(cohortArg.filter)}
        </>
      );
    }

    if (cohortArg.__typename === 'PerformedCohort') {
      return (
        <>
          <PerformedCohortDetails
            cohort={cohortArg}
            cohortIndex={cohortIndex}
          />
          {renderFilterDetails(cohortArg.filter)}
        </>
      );
    }

    if (cohortArg.__typename === 'RetentionCohort') {
      return (
        <>
          <RetentionCohortDetails
            cohort={cohortArg}
            cohortIndex={cohortIndex}
          />
          {renderFilterDetails(cohortArg.filter)}
        </>
      );
    }

    return null;
  };

  const renderCohortDetails = () => {
    if (cohort.__typename === 'CompoundCohort') {
      const { simpleCohorts, logicOps } = cohort;
      return (
        <>
          {simpleCohorts.map((simpleCohort, index) => {
            if (index > 0) {
              assertNotNil(sampleOperation || logicOps);
            }
            return (
              <Fragment key={`${simpleCohort.type.key}-${index}-details`}>
                {index > 0 && (
                  <ConnectorContainer>
                    <StyledOperation>
                      {sampleOperation
                        ? sampleOperation?.key
                        : logicOps[index - 1].key}
                    </StyledOperation>
                  </ConnectorContainer>
                )}
                {renderCohortDetailsByType(simpleCohort, index)}
              </Fragment>
            );
          })}
        </>
      );
    }

    return renderCohortDetailsByType(cohort, 0);
  };

  return (
    <Container>
      <BuilderInfoUI.HeaderContainer>
        <InfoPopupHeaderSetting label={'Schema'} hideSeparator>
          {schema.name}
        </InfoPopupHeaderSetting>
        {(samplePercentage || sampleAbsolute) && (
          <InfoPopupHeaderSetting>
            {typeof samplePercentage === 'number' && `${samplePercentage}%`}
            {typeof sampleAbsolute === 'number' && sampleAbsolute}
          </InfoPopupHeaderSetting>
        )}
      </BuilderInfoUI.HeaderContainer>
      {renderCohortDetails()}
    </Container>
  );
};
